import type {
  DailySalesDay,
  DailySalesReport,
  SalesDateRange,
  SalesTotals,
} from '../../../api/daily-sales';
import { formatVenueDate } from '../../../utils/venueTime';
import type { KpiCell } from '../primitives';

import { daysIn } from './daily-sales-range';

export type SourceKey = 'fareharbor' | 'giftCards' | 'nebari';
export type Measure = keyof SalesTotals;

export const SOURCES: ReadonlyArray<{ key: SourceKey; name: string; color: string }> = [
  { key: 'fareharbor', name: 'FareHarbor', color: 'var(--nb-info)' },
  { key: 'giftCards', name: 'FareHarbor gift cards', color: 'var(--nb-gold)' },
  { key: 'nebari', name: 'Nebari', color: 'var(--nb-primary)' },
];

const ALL_SOURCES: SourceKey[] = ['fareharbor', 'giftCards', 'nebari'];

const ZERO: SalesTotals = { bookings: 0, tickets: 0, grossCents: 0, subtotalCents: 0 };

/** A source's totals for the day, as zeros when the source could not be read. */
export function totalsOf(day: DailySalesDay, source: SourceKey): SalesTotals {
  return day[source] ?? ZERO;
}

export function dayTotal(day: DailySalesDay, measure: Measure, sources = ALL_SOURCES): number {
  return sources.reduce((sum, s) => sum + totalsOf(day, s)[measure], 0);
}

export function windowTotal(days: DailySalesDay[], source: SourceKey, measure: Measure): number {
  return days.reduce((sum, d) => sum + totalsOf(d, source)[measure], 0);
}

/** The whole window for the given sources, added together. */
export function windowSum(days: DailySalesDay[], measure: Measure, sources = ALL_SOURCES): number {
  return sources.reduce((sum, s) => sum + windowTotal(days, s, measure), 0);
}

/** One row per day with a column per source, the shape a stacked bar chart reads. */
export function chartRows(days: DailySalesDay[], sources: SourceKey[], measure: Measure) {
  return days.map((d) => ({
    date: d.date,
    ...Object.fromEntries(sources.map((s) => [s, totalsOf(d, s)[measure]])),
  }));
}

export function formatMoney(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

/** An axis label in thousands: `$2.5K`, `$10K`. */
export function formatMoneyAxis(cents: number): string {
  const thousands = cents / 100_000;
  if (thousands === 0) return '$0';
  return `$${Number.isInteger(thousands) ? thousands : thousands.toFixed(1)}K`;
}

export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** `Oct 8` from `2026-10-08`. Calendar dates carry no time zone, so none is applied. */
export function shortDate(date: string): string {
  return formatVenueDate(date, 'MMM d');
}

export function weekdayName(date: string, length: 'short' | 'long' = 'short'): string {
  return formatVenueDate(date, length === 'short' ? 'EEE' : 'EEEE');
}

export function longDate(date: string): string {
  return formatVenueDate(date, 'MMM d, yyyy');
}

/** A moment, as staff in the head office read it: `Oct 9, 1:48 PM ET`. */
export function easternTime(iso: string): string {
  const text = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
  return `${text} ET`;
}

function change(current: number, previous: number): Pick<KpiCell, 'delta' | 'good'> {
  if (!previous) return {};
  const pct = Math.round(((current - previous) / previous) * 100);
  return { delta: `${pct >= 0 ? '+' : '−'}${Math.abs(pct)}%`, good: pct >= 0 };
}

/** The share of ticket bookings Nebari took, from its first sale in the window. */
export function nebariShare(days: DailySalesDay[]): { pct: number; since: string } | null {
  const first = days.findIndex((d) => d.nebari.bookings > 0);
  if (first < 0) return null;
  const since = days.slice(first);
  const nebari = windowTotal(since, 'nebari', 'bookings');
  const all = windowSum(since, 'bookings', ['fareharbor', 'nebari']);
  return { pct: (nebari / all) * 100, since: days[first].date };
}

export function rangeLabel(range: SalesDateRange): string {
  const days = daysIn(range);
  return `${shortDate(range.from)} – ${longDate(range.to)} · ${days} ${days === 1 ? 'day' : 'days'}`;
}

/** The summary day against the same weekday a week before. It is yesterday while the
 *  range runs to today, and is then named that way; otherwise it is named by its date. */
function kpiDayCells(report: DailySalesReport): KpiCell[] {
  const day = report.days.find((d) => d.date === report.kpiDay);
  if (!day) return [];
  const weekBefore = report.kpiWeekBefore;
  const name =
    report.to === report.today ? 'Yesterday' : `${weekdayName(day.date)} ${shortDate(day.date)}`;
  const vs = weekBefore ? `vs ${weekdayName(weekBefore.date)} ${shortDate(weekBefore.date)}` : '';
  const bookings = dayTotal(day, 'bookings');
  const gross = dayTotal(day, 'grossCents');
  return [
    {
      label: `${name} · bookings`,
      value: formatCount(bookings),
      ...(weekBefore ? change(bookings, dayTotal(weekBefore, 'bookings')) : {}),
      sub: weekBefore ? vs : weekdayName(day.date, 'long'),
    },
    {
      label: `${name} · gross payment`,
      value: formatMoney(gross),
      ...(weekBefore ? change(gross, dayTotal(weekBefore, 'grossCents')) : {}),
      sub: vs || undefined,
    },
  ];
}

/** The summary day, then the whole window. */
export function summaryCells(report: DailySalesReport): KpiCell[] {
  const { days } = report;
  const cells: KpiCell[] = kpiDayCells(report);

  const tickets = windowSum(days, 'tickets', ['fareharbor', 'nebari']);
  const giftCards = windowTotal(days, 'giftCards', 'tickets');
  cells.push(
    {
      label: `${days.length} days · bookings`,
      value: formatCount(windowSum(days, 'bookings')),
      sub: `${formatCount(tickets)} tickets · ${formatCount(giftCards)} gift cards`,
    },
    {
      label: `${days.length} days · gross payment`,
      value: formatMoney(windowSum(days, 'grossCents')),
      sub: `${formatMoney(windowSum(days, 'subtotalCents'))} pre-tax`,
    },
  );

  const share = nebariShare(days);
  if (share) {
    cells.push({
      label: 'Nebari share of ticket bookings',
      value: `${share.pct.toFixed(1)}%`,
      sub: `since ${shortDate(share.since)}`,
    });
  }
  return cells;
}
